import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import AppModal from "../AppModal";
import BookingChannelFields, {
	formatChannelLabel,
	resolveChannelPayload,
} from "../BookingChannelFields";

function pad(n) {
	return String(n).padStart(2, "0");
}

function toLocalInputValue(date) {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
		date.getHours()
	)}:${pad(date.getMinutes())}`;
}

function defaultFormValues() {
	const start = new Date();
	start.setMinutes(0, 0, 0);
	start.setHours(start.getHours() + 1);
	const end = new Date(start.getTime() + 30 * 60 * 1000);
	return {
		title: "",
		description: "",
		start: toLocalInputValue(start),
		end: toLocalInputValue(end),
		channelKey: "unknown",
		channelOther: "",
		referredBy: "",
	};
}

function formatWhen(raw) {
	if (!raw) return "—";
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return String(raw);
	return d.toLocaleString(undefined, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function sourceLabel(booking) {
	const name = String(booking?.createdByName || "").trim();
	if (name) return name;
	const value = String(booking?.source || "").toLowerCase();
	if (value === "ai") return i18n.t("contactDrawer.bookings.sourceAi");
	return i18n.t("contactDrawer.bookings.sourceManual");
}

function statusLabel(status) {
	const value = String(status || "scheduled").toLowerCase();
	if (value === "completed") return i18n.t("contactDrawer.bookings.statusCompleted");
	if (value === "cancelled") return i18n.t("contactDrawer.bookings.statusCancelled");
	if (value === "postponed") return i18n.t("contactDrawer.bookings.statusPostponed");
	return i18n.t("contactDrawer.bookings.statusScheduled");
}

function statusBadgeClass(status) {
	const value = String(status || "scheduled").toLowerCase();
	if (value === "completed") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
	if (value === "cancelled") return "bg-rose-500/15 text-rose-700 dark:text-rose-300";
	if (value === "postponed") return "bg-amber-500/15 text-amber-800 dark:text-amber-300";
	return "bg-sky-500/15 text-sky-800 dark:text-sky-300";
}

export default function ContactDrawerBookings({ contact, ticketId }) {
	const contactId = contact?.id;
	const [bookings, setBookings] = useState([]);
	const [connected, setConnected] = useState(true);
	const [loading, setLoading] = useState(false);
	const [adding, setAdding] = useState(false);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState(defaultFormValues);
	const [selected, setSelected] = useState(null);
	const [editStatus, setEditStatus] = useState("scheduled");
	const [editReason, setEditReason] = useState("");
	const [editStart, setEditStart] = useState("");
	const [editEnd, setEditEnd] = useState("");
	const [savingStatus, setSavingStatus] = useState(false);

	const load = useCallback(async () => {
		if (!contactId) return;
		setLoading(true);
		try {
			const { data } = await apiV2.get("/google-calendar/bookings", {
				params: { contactId },
			});
			setConnected(data?.connected !== false);
			setBookings(Array.isArray(data?.bookings) ? data.bookings : []);
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("contactDrawer.bookings.loadError"));
			setBookings([]);
		} finally {
			setLoading(false);
		}
	}, [contactId]);

	useEffect(() => {
		load();
	}, [load]);

	const visibleBookings = useMemo(() => bookings.slice(0, 3), [bookings]);
	const hasMore = bookings.length > 3;

	const seeAllHref = useMemo(() => {
		const params = new URLSearchParams();
		if (contactId) params.set("contactId", String(contactId));
		if (contact?.name) params.set("name", contact.name);
		if (contact?.number) params.set("phone", String(contact.number));
		const qs = params.toString();
		return qs ? `/google-agenda/relatorio?${qs}` : "/google-agenda/relatorio";
	}, [contactId, contact?.name, contact?.number]);

	const handleSave = async (event) => {
		event.preventDefault();
		if (!contactId || saving) return;

		const title = form.title.trim();
		const description = form.description.trim();
		if (!title) {
			toast.error(i18n.t("contactDrawer.bookings.titleLabel"));
			return;
		}

		const { acquisitionChannel, referredBy } = resolveChannelPayload(
			form.channelKey,
			form.channelOther,
			form.referredBy
		);

		setSaving(true);
		try {
			await apiV2.post("/google-calendar/bookings", {
				title,
				description,
				start: new Date(form.start).toISOString(),
				end: new Date(form.end).toISOString(),
				source: "manual",
				contactId,
				acquisitionChannel,
				referredBy,
				...(ticketId ? { ticketId } : {}),
			});
			toast.success(i18n.t("contactDrawer.bookings.saveSuccess"));
			setAdding(false);
			setForm(defaultFormValues());
			await load();
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("contactDrawer.bookings.saveError"));
		} finally {
			setSaving(false);
		}
	};

	const openBooking = (booking) => {
		setSelected(booking);
		setEditStatus(String(booking?.status || "scheduled").toLowerCase() || "scheduled");
		setEditReason(booking?.statusReason || "");
		setEditStart(booking?.startsAt ? toLocalInputValue(new Date(booking.startsAt)) : "");
		setEditEnd(booking?.endsAt ? toLocalInputValue(new Date(booking.endsAt)) : "");
	};

	const saveStatus = async () => {
		if (!selected?.id || savingStatus) return;
		if (editStatus === "postponed") {
			if (!editStart || !editEnd) {
				toast.error(i18n.t("contactDrawer.bookings.postponeRequired"));
				return;
			}
			if (new Date(editEnd).getTime() <= new Date(editStart).getTime()) {
				toast.error(i18n.t("contactDrawer.bookings.postponeInvalidRange"));
				return;
			}
		}
		setSavingStatus(true);
		try {
			const payload = {
				status: editStatus,
				reason: editReason,
			};
			if (editStatus === "postponed") {
				payload.start = new Date(editStart).toISOString();
				payload.end = new Date(editEnd).toISOString();
			}
			await apiV2.put(`/google-calendar/bookings/${selected.id}/status`, payload);
			toast.success(i18n.t("contactDrawer.bookings.statusSaved"));
			setSelected(null);
			await load();
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("contactDrawer.bookings.statusSaveError"));
		} finally {
			setSavingStatus(false);
		}
	};

	return (
		<div className="tailwick-contact-drawer__section">
			<div className="mb-2 flex items-center justify-between gap-2">
				<p className="tailwick-filter-panel__section-title mb-0">
					{i18n.t("contactDrawer.bookings.title")}
				</p>
				{connected && (
					<button
						type="button"
						className="btn btn-sm border border-primary/40 bg-primary/15 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/25"
						onClick={() => {
							setAdding((open) => !open);
							setForm(defaultFormValues());
						}}
					>
						{adding
							? i18n.t("contactDrawer.bookings.cancel")
							: i18n.t("contactDrawer.bookings.add")}
					</button>
				)}
			</div>

			{!connected ? (
				<p className="tailwick-filter-panel__empty text-xs">
					{i18n.t("contactDrawer.bookings.disconnected")}
				</p>
			) : loading ? (
				<p className="tailwick-filter-panel__empty text-xs">
					{i18n.t("contactDrawer.bookings.loading")}
				</p>
			) : (
				<>
					{adding && (
						<form
							onSubmit={handleSave}
							className="mb-3 w-full min-w-0 max-w-full space-y-2 overflow-hidden rounded-lg border border-default-200 bg-default-50/70 p-2.5 dark:border-default-700 dark:bg-default-900"
						>
							<label className="block w-full min-w-0 text-xs text-default-600 dark:text-default-300">
								{i18n.t("contactDrawer.bookings.titleLabel")}
								<input
									className="form-input mt-1 w-full min-w-0 max-w-full"
									value={form.title}
									onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
									required
								/>
							</label>
							<label className="block w-full min-w-0 text-xs text-default-600 dark:text-default-300">
								{i18n.t("contactDrawer.bookings.descriptionLabel")}
								<textarea
									className="form-textarea mt-1 min-h-[4rem] w-full min-w-0 max-w-full"
									value={form.description}
									onChange={(e) =>
										setForm((prev) => ({ ...prev, description: e.target.value }))
									}
								/>
							</label>
							<label className="block w-full min-w-0 text-xs text-default-600 dark:text-default-300">
								{i18n.t("contactDrawer.bookings.startLabel")}
								<input
									type="datetime-local"
									className="form-input mt-1 w-full min-w-0 max-w-full"
									value={form.start}
									onChange={(e) => setForm((prev) => ({ ...prev, start: e.target.value }))}
									required
								/>
							</label>
							<label className="block w-full min-w-0 text-xs text-default-600 dark:text-default-300">
								{i18n.t("contactDrawer.bookings.endLabel")}
								<input
									type="datetime-local"
									className="form-input mt-1 w-full min-w-0 max-w-full"
									value={form.end}
									onChange={(e) => setForm((prev) => ({ ...prev, end: e.target.value }))}
									required
								/>
							</label>
							<BookingChannelFields
								channelKey={form.channelKey}
								channelOther={form.channelOther}
								referredBy={form.referredBy}
								onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
							/>
							<button
								type="submit"
								className="btn btn-sm w-full bg-primary text-white"
								disabled={saving}
							>
								{i18n.t("contactDrawer.bookings.save")}
							</button>
						</form>
					)}

					{bookings.length === 0 ? (
						<p className="tailwick-filter-panel__empty text-xs">
							{i18n.t("contactDrawer.bookings.empty")}
						</p>
					) : (
						<>
							<ul className="m-0 flex list-none flex-col gap-2 p-0">
								{visibleBookings.map((booking) => (
									<li key={booking.id}>
										<button
											type="button"
											className="w-full rounded-lg border border-default-200 bg-card px-2.5 py-2 text-left transition hover:border-primary/40 hover:bg-primary/5 dark:border-default-700 dark:bg-default-900 dark:hover:bg-primary/10"
											onClick={() => openBooking(booking)}
										>
											<div className="mb-1 flex items-start justify-between gap-2">
												<p className="m-0 text-sm font-semibold text-default-900 dark:text-default-50">
													{booking.title || "—"}
												</p>
												<span
													className={`shrink-0 rounded-md px-1.5 py-0.5 text-[0.65rem] font-semibold ${statusBadgeClass(
														booking.status
													)}`}
												>
													{statusLabel(booking.status)}
												</span>
											</div>
											{booking.description ? (
												<p className="mb-1 mt-0 text-xs text-default-600 dark:text-default-300">
													{booking.description}
												</p>
											) : null}
											<p className="m-0 text-xs text-default-500">
												{formatWhen(booking.startsAt)} → {formatWhen(booking.endsAt)}
											</p>
											<p
												className="mb-0 mt-1 truncate text-[0.65rem] text-default-400"
												title={formatChannelLabel(
													booking.acquisitionChannel,
													booking.referredBy
												)}
											>
												{i18n.t("contactDrawer.bookings.channelLabel")}:{" "}
												{formatChannelLabel(
													booking.acquisitionChannel,
													booking.referredBy
												)}
											</p>
											{booking.reminderScheduleId ? (
												<p className="mb-0 mt-1 text-[0.65rem] text-primary">
													{i18n.t("contactDrawer.bookings.reminderScheduled")}
												</p>
											) : null}
											<p className="mb-0 mt-1 text-[0.65rem] text-default-400">
												{sourceLabel(booking)}
												{booking.statusChangedByName
													? ` · ${i18n.t("contactDrawer.bookings.changedBy")}: ${
															booking.statusChangedByName
													  }`
													: ""}
											</p>
										</button>
									</li>
								))}
							</ul>
							{hasMore ? (
								<Link
									to={seeAllHref}
									className="mt-2 inline-flex w-full items-center justify-center rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-2 text-xs font-semibold text-primary hover:bg-primary/20"
								>
									{i18n.t("contactDrawer.bookings.seeAll")} ({bookings.length})
								</Link>
							) : null}
						</>
					)}
				</>
			)}

			<AppModal
				open={Boolean(selected)}
				onClose={() => !savingStatus && setSelected(null)}
				title={selected?.title || i18n.t("contactDrawer.bookings.title")}
				subtitle={formatWhen(selected?.startsAt)}
				maxWidth={480}
				footer={
					<div className="flex flex-wrap justify-end gap-2">
						<button
							type="button"
							className="btn btn-sm border border-default-300 bg-transparent dark:border-default-600"
							onClick={() => setSelected(null)}
							disabled={savingStatus}
						>
							{i18n.t("contactDrawer.bookings.cancel")}
						</button>
						<button
							type="button"
							className="btn btn-sm bg-primary text-white"
							onClick={saveStatus}
							disabled={savingStatus}
						>
							{i18n.t("contactDrawer.bookings.save")}
						</button>
					</div>
				}
			>
				{selected && (
					<div className="space-y-3">
						<div className="rounded-lg border border-default-200 bg-default-50/80 p-3 dark:border-default-700 dark:bg-default-900">
							<p className="mb-1 mt-0 text-xs text-default-500">
								{formatWhen(selected.startsAt)} → {formatWhen(selected.endsAt)}
							</p>
							{selected.description ? (
								<p className="mb-0 whitespace-pre-wrap text-sm text-default-800 dark:text-default-200">
									{selected.description}
								</p>
							) : null}
							<p className="mb-0 mt-2 text-xs text-default-500">
								{i18n.t("contactDrawer.bookings.channelLabel")}:{" "}
								{formatChannelLabel(selected.acquisitionChannel, selected.referredBy)}
							</p>
							{selected.statusChangedByName ? (
								<p className="mb-0 mt-2 text-xs text-default-500">
									{String(selected.status).toLowerCase() === "postponed"
										? i18n.t("contactDrawer.bookings.postponedBy")
										: i18n.t("contactDrawer.bookings.changedBy")}
									: {selected.statusChangedByName}
								</p>
							) : null}
							{selected.statusReason ? (
								<p className="mb-0 mt-1 text-xs text-default-500">
									{i18n.t("contactDrawer.bookings.reason")}: {selected.statusReason}
								</p>
							) : null}
						</div>
						<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
							{i18n.t("contactDrawer.bookings.changeStatus")}
							<select
								className="form-select mt-1"
								value={editStatus}
								onChange={(e) => setEditStatus(e.target.value)}
							>
								<option value="scheduled">
									{i18n.t("contactDrawer.bookings.statusScheduled")}
								</option>
								<option value="completed">
									{i18n.t("contactDrawer.bookings.statusCompleted")}
								</option>
								<option value="postponed">
									{i18n.t("contactDrawer.bookings.statusPostponed")}
								</option>
								<option value="cancelled">
									{i18n.t("contactDrawer.bookings.statusCancelled")}
								</option>
							</select>
						</label>
						{editStatus === "postponed" ? (
							<>
								<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
									{i18n.t("contactDrawer.bookings.newStart")}
									<input
										type="datetime-local"
										className="form-input mt-1"
										value={editStart}
										onChange={(e) => setEditStart(e.target.value)}
										required
									/>
								</label>
								<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
									{i18n.t("contactDrawer.bookings.newEnd")}
									<input
										type="datetime-local"
										className="form-input mt-1"
										value={editEnd}
										onChange={(e) => setEditEnd(e.target.value)}
										required
									/>
								</label>
							</>
						) : null}
						<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
							{i18n.t("contactDrawer.bookings.reasonOptional")}
							<textarea
								className="form-textarea mt-1 min-h-[4rem]"
								value={editReason}
								onChange={(e) => setEditReason(e.target.value)}
							/>
						</label>
					</div>
				)}
			</AppModal>
		</div>
	);
}
